import {
  CreateVaultMessageRequest, UpdateVaultMessageRequest, VaultMessage
} from '../../types/vault';

// Dummy data for demo purposes
const dummyVaultMessages: VaultMessage[] = [
  {
    id: '1',
    title: 'My Final Words to Sarah',
    content: 'My dearest Sarah, I want you to know how much I love you and how proud I am of the person you\'ve become...',
    isLocked: true,
    recipients: [
      { id: '1', firstName: 'Sarah', lastName: 'Johnson', relationship: 'daughter' }
    ],
    createdBy: 'user-1',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    title: 'Instructions for My Collection',
    content: 'Please ensure that my vintage book collection goes to the local library...',
    isLocked: false,
    recipients: [
      { id: '2', firstName: 'Michael', lastName: 'Smith', relationship: 'son' },
      { id: '3', firstName: 'Emma', lastName: 'Davis', relationship: 'daughter' }
    ],
    createdBy: 'user-1',
    createdAt: '2024-01-10T14:20:00Z',
    updatedAt: '2024-01-10T14:20:00Z'
  },
  {
    id: '3',
    title: 'Secret Family Recipe',
    content: 'The secret ingredient in grandma\'s apple pie is...',
    isLocked: true,
    recipients: [
      { id: '4', firstName: 'Lisa', lastName: 'Wilson', relationship: 'sister' }
    ],
    createdBy: 'user-1',
    createdAt: '2024-01-05T09:15:00Z',
    updatedAt: '2024-01-05T09:15:00Z'
  }
];

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const vaultClient = {
  async findAll(): Promise<VaultMessage[]> {
    await delay(500);
    return [ ...dummyVaultMessages ];
  },

  async find(id: string): Promise<VaultMessage | null> {
    await delay(300);
    return dummyVaultMessages.find(message => message.id === id) || null;
  },

  async create(data: CreateVaultMessageRequest): Promise<VaultMessage> {
    await delay(800);
    const newMessage: VaultMessage = {
      id: Date.now().toString(),
      title: data.title,
      content: data.content,
      password: data.password,
      isLocked: !!data.password,
      recipients: data.recipientIds.map(id => ({ 
        id, 
        firstName: 'Unknown', 
        lastName: 'Person',
        relationship: 'unknown'
      })), // In real app, would fetch from people API
      createdBy: 'user-1', // In real app, would get from auth context
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    dummyVaultMessages.unshift(newMessage);
    return newMessage;
  },

  async update(id: string, data: UpdateVaultMessageRequest): Promise<VaultMessage | null> {
    await delay(600);
    const messageIndex = dummyVaultMessages.findIndex(message => message.id === id);
    
    if (messageIndex === -1) {
      return null;
    }

    const updatedMessage = {
      ...dummyVaultMessages[messageIndex],
      ...data,
      isLocked: !!data.password || dummyVaultMessages[messageIndex].isLocked,
      updatedAt: new Date().toISOString()
    };

    dummyVaultMessages[messageIndex] = updatedMessage;
    return updatedMessage;
  },

  async destroy(id: string): Promise<boolean> {
    await delay(400);
    const messageIndex = dummyVaultMessages.findIndex(message => message.id === id);
    
    if (messageIndex === -1) {
      return false;
    }

    dummyVaultMessages.splice(messageIndex, 1);
    return true;
  },

  async unlockMessage(id: string, password: string): Promise<boolean> {
    await delay(300);
    const message = dummyVaultMessages.find(msg => msg.id === id);
    
    if (!message || !message.password) {
      return false;
    }

    // In a real app, you'd hash the password and compare
    return message.password === password;
  }
};
